/**
 * LEGAL-13 — Tab „Parametrierung": cost configuration only, no computation.
 *
 * Per-role rate table (mode Pauschal/Zeit; Anfahrt always flat; Durchführung
 * shows / Asset for flat, / h for time), geo-override sub-rows per role
 * (Bundesland), and duration defaults per checklist type. Saving appends a new
 * tenant-scoped config version.
 */

import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai";
import { Globe, Loader2, Plus, Trash2 } from "lucide-react";

import Section from "../../components/Section";
import { useModalContext } from "../../components/ModalProvider";
import { authenticationAtom } from "../../State";
import { useLegalBindingAPI } from "../../LegalBindingAPI";
import { QUALIFICATION_ROLE_IDS, qualificationRoleName } from "../../administration/operator-responsibility/legalDisplay";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Properties {
  canWrite: boolean;
  onConfigurationSaved?: () => void;
}

const BUNDESLAENDER = [
  "BW", "BY", "BE", "BB", "HB", "HH", "HE", "MV", "NI", "NW", "RP", "SL", "SN", "ST", "SH", "TH",
];

const num = (value: string | undefined): number | undefined => {
  if (value === undefined || value === "") return undefined;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
};

export default function CostParametrizationPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const [authentication] = useAtom(authenticationAtom) as any;
  const legalAPI = useLegalBindingAPI(authentication?.customer);
  // Measured durations (median of worked checklists, own and all tenants) — read-only, recomputed nightly.
  const [measured, setMeasured] = useState<{ minSamples: number; items: any[] } | null>(null);
  useEffect(() => {
    if (!authentication?.customer) return;
    legalAPI
      .getChecklistDurations()
      .then(setMeasured)
      .catch(() => setMeasured(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  const [roleCosts, setRoleCosts] = useState<any[]>([]);
  const [durationDefaults, setDurationDefaults] = useState<any[]>([]);
  const [version, setVersion] = useState(0);
  const [configurationLoading, setConfigurationLoading] = useState(false);
  const [configurationLoaded, setConfigurationLoaded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const unmounted = useRef(false);

  const canUpdateState = (cancelled?: { current: boolean }) => !unmounted.current && !cancelled?.current;
  const canEditConfiguration = properties.canWrite && configurationLoaded && !configurationLoading && !submitting;

  const load = async (cancelled?: { current: boolean }) => {
    if (canUpdateState(cancelled)) {
      setConfigurationLoading(true);
      setConfigurationLoaded(false);
    }

    try {
      const config = await legalAPI.getCostConfiguration();
      if (canUpdateState(cancelled)) {
        setRoleCosts(config.roleCosts || []);
        setDurationDefaults(config.durationDefaults || []);
        setVersion(config.version || 0);
        setConfigurationLoaded(true);
      }
    } catch (error) {
      if (canUpdateState(cancelled)) {
        pushError(error);
        setConfigurationLoaded(false);
      }
    } finally {
      if (canUpdateState(cancelled)) {
        setConfigurationLoading(false);
      }
    }
  };

  useEffect(() => {
    unmounted.current = false;

    return () => {
      unmounted.current = true;
    };
  }, []);

  useEffect(() => {
    const cancelled = { current: false };
    if (authentication?.customer) {
      setRoleCosts([]);
      setDurationDefaults([]);
      setVersion(0);
      setConfigurationLoaded(false);
      load(cancelled);
    } else {
      setRoleCosts([]);
      setDurationDefaults([]);
      setVersion(0);
      setConfigurationLoaded(false);
      setConfigurationLoading(false);
    }
    return () => {
      cancelled.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authentication?.customer]);

  const roleCostFor = (role: string) => roleCosts.find((entry) => entry.role === role);

  const updateRole = (role: string, patch: any) => {
    setRoleCosts((prev) => {
      const existing = prev.find((entry) => entry.role === role);
      if (existing) {
        return prev.map((entry) => (entry.role === role ? { ...entry, ...patch } : entry));
      }
      return [...prev, { role, mode: "flat", callOutFee: 0, geoOverrides: [], ...patch }];
    });
  };

  const addGeoOverride = (role: string) => {
    const entry = roleCostFor(role);
    const used = new Set((entry?.geoOverrides || []).map((o: any) => o.geo));
    const geo = BUNDESLAENDER.find((b) => !used.has(b));
    if (!geo) return;
    updateRole(role, { geoOverrides: [...(entry?.geoOverrides || []), { geo }] });
  };

  const updateGeoOverride = (role: string, index: number, patch: any) => {
    const entry = roleCostFor(role);
    const overrides = [...(entry?.geoOverrides || [])];
    if (patch.geo !== undefined) {
      const existingIndex = overrides.findIndex((o: any, i: number) => i !== index && o.geo === patch.geo);
      if (existingIndex !== -1) return;
    }
    overrides[index] = { ...overrides[index], ...patch };
    updateRole(role, { geoOverrides: overrides });
  };

  const removeGeoOverride = (role: string, index: number) => {
    const entry = roleCostFor(role);
    updateRole(role, { geoOverrides: (entry?.geoOverrides || []).filter((_: any, i: number) => i !== index) });
  };

  const save = async () => {
    if (!properties.canWrite || submitting || configurationLoading || !configurationLoaded) {
      return;
    }

    setSubmitting(true);

    try {
      // Persist only roles the user actually configured (any non-zero field).
      const cleaned = roleCosts.filter(
        (entry) => entry.callOutFee || entry.executionFee || entry.hourlyRate || (entry.geoOverrides || []).length,
      );
      await legalAPI.saveCostConfiguration({
        roleCosts: cleaned,
        durationDefaults: durationDefaults.filter((entry) => entry.checklistRef && entry.durationMinutes),
      });
      await load();
      if (canUpdateState() && properties.onConfigurationSaved) {
        properties.onConfigurationSaved();
      }
    } catch (error) {
      if (canUpdateState()) {
        pushError(error);
      }
    } finally {
      if (canUpdateState()) {
        setSubmitting(false);
      }
    }
  };

  /**
   * Zahlenfeld einer Tabellenzelle. Die Spaltenüberschrift trägt die Bedeutung
   * visuell; für Screenreader wiederholt `aria-label` sie zusammen mit der
   * Zeile, weil eine Zelle allein („90") nichts aussagt.
   */
  const numField = (
    value: any,
    onChange: (v: string) => void,
    ariaLabel: string,
    suffix?: string,
  ) => (
    <div className="flex items-center gap-2">
      <Input
        className="w-24"
        inputMode="decimal"
        aria-label={ariaLabel}
        value={value === undefined || value === null ? "" : String(value)}
        disabled={!canEditConfiguration}
        onChange={(event) => onChange(event.target.value || "")}
      />
      {suffix ? <span className="whitespace-nowrap text-xs text-muted-foreground">{suffix}</span> : null}
    </div>
  );

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <p className="min-w-0 break-words text-sm text-muted-foreground">
          {t("operatorObligationsPanel.parametrizationNote", { version })}
        </p>
        {!configurationLoading && version === 0 ? (
          <p className="min-w-0 break-words text-sm text-muted-foreground" data-testid="cost-presets-note">
            {t("operatorObligationsPanel.costPresetsNote")}
          </p>
        ) : null}
        {configurationLoading ? (
          <span className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
            <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
            {t("global.loading")}
          </span>
        ) : null}
      </div>

      <Section title={t("operatorObligationsPanel.roleCosts")}>
        <div className="min-w-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("operatorResponsibilityPanel.roleLabel")}</TableHead>
                <TableHead>{t("operatorObligationsPanel.mode")}</TableHead>
                <TableHead>{t("operatorObligationsPanel.callOutFee")}</TableHead>
                <TableHead>{t("operatorObligationsPanel.executionFee")}</TableHead>
                <TableHead>
                  <span className="sr-only">{t("operatorObligationsPanel.addGeoOverride")}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {QUALIFICATION_ROLE_IDS.map((role) => {
                const entry = roleCostFor(role) || { mode: "flat", callOutFee: 0, geoOverrides: [] };
                const isTime = entry.mode === "time";
                const roleName = qualificationRoleName(t, role);
                return (
                  <Fragment key={role}>
                    <TableRow>
                      <TableCell className="min-w-0 break-words font-semibold">{roleName}</TableCell>
                      <TableCell>
                        <Select
                          value={entry.mode}
                          disabled={!canEditConfiguration}
                          onValueChange={(value: string) => {
                            const patch: any = { mode: value };
                            if (value === "flat") {
                              patch.hourlyRate = undefined;
                              patch.geoOverrides = (entry.geoOverrides || []).map((o: any) => {
                                const { hourlyRate, ...rest } = o;
                                return rest;
                              });
                            } else {
                              patch.executionFee = undefined;
                              patch.geoOverrides = (entry.geoOverrides || []).map((o: any) => {
                                const { executionFee, ...rest } = o;
                                return rest;
                              });
                            }
                            updateRole(role, patch);
                          }}
                        >
                          <SelectTrigger
                            className="w-[130px]"
                            aria-label={`${t("operatorObligationsPanel.mode")} – ${roleName}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="flat">{t("operatorObligationsPanel.modeFlat")}</SelectItem>
                            <SelectItem value="time">{t("operatorObligationsPanel.modeTime")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {numField(
                          entry.callOutFee,
                          (v) => updateRole(role, { callOutFee: num(v) ?? 0 }),
                          `${t("operatorObligationsPanel.callOutFee")} – ${roleName}`,
                          "€",
                        )}
                      </TableCell>
                      <TableCell>
                        {isTime
                          ? numField(
                              entry.hourlyRate,
                              (v) => updateRole(role, { hourlyRate: num(v) }),
                              `${t("operatorObligationsPanel.executionFee")} – ${roleName}`,
                              "€ / h",
                            )
                          : numField(
                              entry.executionFee,
                              (v) => updateRole(role, { executionFee: num(v) }),
                              `${t("operatorObligationsPanel.executionFee")} – ${roleName}`,
                              "€ / Asset",
                            )}
                      </TableCell>
                      <TableCell>
                        {properties.canWrite ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-2 whitespace-nowrap"
                            disabled={!canEditConfiguration}
                            onClick={() => addGeoOverride(role)}
                          >
                            <Globe aria-hidden className="h-4 w-4" />
                            {t("operatorObligationsPanel.addGeoOverride")}
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                    {(entry.geoOverrides || []).map((override: any, index: number) => (
                      <TableRow key={`${role}-${index}`} className="bg-muted/50">
                        <TableCell className="pl-8 text-xs text-muted-foreground">
                          {t("operatorObligationsPanel.geoOverride")}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={override.geo}
                            disabled={!canEditConfiguration}
                            onValueChange={(value: string) => updateGeoOverride(role, index, { geo: value })}
                          >
                            <SelectTrigger
                              className="w-[90px]"
                              aria-label={`${t("operatorObligationsPanel.geoOverride")} – ${roleName}`}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {BUNDESLAENDER.map((b) => (
                                <SelectItem key={b} value={b}>{b}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {numField(
                            override.callOutFee,
                            (v) => updateGeoOverride(role, index, { callOutFee: num(v) }),
                            `${t("operatorObligationsPanel.callOutFee")} – ${roleName} – ${override.geo || ""}`,
                            "€",
                          )}
                        </TableCell>
                        <TableCell>
                          {isTime
                            ? numField(
                                override.hourlyRate,
                                (v) => updateGeoOverride(role, index, { hourlyRate: num(v) }),
                                `${t("operatorObligationsPanel.executionFee")} – ${roleName} – ${override.geo || ""}`,
                                "€ / h",
                              )
                            : numField(
                                override.executionFee,
                                (v) => updateGeoOverride(role, index, { executionFee: num(v) }),
                                `${t("operatorObligationsPanel.executionFee")} – ${roleName} – ${override.geo || ""}`,
                                "€ / Asset",
                              )}
                        </TableCell>
                        <TableCell>
                          {properties.canWrite ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={!canEditConfiguration}
                              aria-label={`${t("global.delete")} – ${t("operatorObligationsPanel.geoOverride")} ${override.geo || ""}`}
                              title={t("global.delete")}
                              onClick={() => removeGeoOverride(role, index)}
                            >
                              <Trash2 aria-hidden className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Section>

      <Section title={t("operatorObligationsPanel.durationDefaults")}>
        <p className="text-sm text-muted-foreground">
          {t("operatorObligationsPanel.durationDefaultsNote")}
        </p>
        <div className="mt-4 min-w-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("operatorObligationsPanel.checklistRef")}</TableHead>
                <TableHead>{t("operatorObligationsPanel.durationMinutes")}</TableHead>
                <TableHead>{t("operatorObligationsPanel.durationSource")}</TableHead>
                <TableHead>
                  <span className="sr-only">{t("global.delete")}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {durationDefaults.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-20 text-center text-sm text-muted-foreground">
                    {t("operatorObligationsPanel.noDurationDefaults")}
                  </TableCell>
                </TableRow>
              ) : null}
              {durationDefaults.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      className="w-60"
                      aria-label={t("operatorObligationsPanel.checklistRef")}
                      value={entry.checklistRef || ""}
                      disabled={!canEditConfiguration}
                      placeholder={t("operatorObligationsPanel.checklistRefPlaceholder")}
                      onChange={(event) =>
                        setDurationDefaults((prev) =>
                          prev.map((d, i) => (i === index ? { ...d, checklistRef: event.target.value || "" } : d)),
                        )
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {numField(
                      entry.durationMinutes,
                      (v) =>
                        setDurationDefaults((prev) =>
                          prev.map((d, i) => (i === index ? { ...d, durationMinutes: num(v) ?? 0 } : d)),
                        ),
                      `${t("operatorObligationsPanel.durationMinutes")} – ${entry.checklistRef || index + 1}`,
                      "min",
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={entry.source === "actuals" ? "text-xs text-success" : "text-xs text-muted-foreground"}>
                      {entry.source === "actuals"
                        ? t("operatorObligationsPanel.durationFromActuals", { n: entry.n ?? 0 })
                        : t("operatorObligationsPanel.durationParametrized")}
                    </span>
                  </TableCell>
                  <TableCell>
                    {properties.canWrite ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        disabled={!canEditConfiguration}
                        aria-label={`${t("global.delete")} – ${entry.checklistRef || t("operatorObligationsPanel.checklistRef")}`}
                        title={t("global.delete")}
                        onClick={() => setDurationDefaults((prev) => prev.filter((_, i) => i !== index))}
                      >
                        <Trash2 aria-hidden className="h-4 w-4" />
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {properties.canWrite ? (
          <div className="mt-3 flex flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 max-sm:w-full"
              disabled={!canEditConfiguration}
              onClick={() =>
                setDurationDefaults((prev) => [...prev, { checklistRef: "", durationMinutes: 30, source: "parametrized" }])
              }
            >
              <Plus aria-hidden className="h-4 w-4" />
              {t("operatorObligationsPanel.addDurationDefault")}
            </Button>
          </div>
        ) : null}
      </Section>

      <Section title={t("operatorObligationsPanel.measuredDurations")}>
        <p className="text-sm text-muted-foreground">
          {t("operatorObligationsPanel.measuredDurationsNote", { n: measured?.minSamples ?? 5 })}
        </p>
        <div className="mt-4 min-w-0 overflow-x-auto" data-testid="measured-durations">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("operatorObligationsPanel.checklistRef")}</TableHead>
                <TableHead>{t("operatorObligationsPanel.measuredScope")}</TableHead>
                <TableHead className="text-right">{t("operatorObligationsPanel.measuredMedian")}</TableHead>
                <TableHead className="text-right">n</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!measured?.items?.length ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-20 text-center text-sm text-muted-foreground">
                    {t("operatorObligationsPanel.noMeasuredDurations")}
                  </TableCell>
                </TableRow>
              ) : (
                measured.items.map((item: any) => (
                  <TableRow key={`${item.scope}-${item.obligationKey}`}>
                    <TableCell className="break-words">{item.obligationKey}</TableCell>
                    <TableCell>{t(`operatorObligationsPanel.measuredScopes.${item.scope}`)}</TableCell>
                    <TableCell className="text-right tabular-nums">{item.medianMinutes} min</TableCell>
                    <TableCell className={item.n >= (measured.minSamples ?? 5) ? "text-right tabular-nums" : "text-right tabular-nums text-muted-foreground"}>
                      {item.n}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Section>

      {properties.canWrite ? (
        <div className="flex flex-wrap">
          <Button className="max-sm:w-full" disabled={!canEditConfiguration} onClick={save}>
            {submitting ? <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t("operatorObligationsPanel.saveConfiguration")}
          </Button>
        </div>
      ) : (
        <Alert variant="warning">
          <AlertDescription>{t("operatorObligationsPanel.writeRequiresAdmin")}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
